import { AdHocReportBuilder } from "../../ad-hoc-report-builder";

export class ResultsUI {
    private parent: AdHocReportBuilder;
    private wrapper: HTMLElement;
    private tableWrapper: HTMLElement;
    private paginationWrapper: HTMLElement;
    private actionsWrapper: HTMLElement;
    private exportToCsvButton: HTMLInputElement;
    private saveReportButton: HTMLInputElement;

    constructor(parent: AdHocReportBuilder, wrapper: HTMLElement) {
        this.parent = parent;
        this.wrapper = wrapper;
    }

    buildUi(): void {
        // Add wrapper for table
        this.tableWrapper = document.createElement("div");
        this.tableWrapper.id = "ahrb-table-data-wrapper";
        this.tableWrapper.classList.add("table-responsive", "mt-5", "mb-3");
        this.wrapper.appendChild(this.tableWrapper);

        // Add wrapper for pagination
        this.paginationWrapper = document.createElement("div");
        this.paginationWrapper.id = "ahrb-table-pagination-wrapper";
        this.paginationWrapper.classList.add("d-flex", "flex-wrap", "justify-content-center");
        this.wrapper.appendChild(this.paginationWrapper);

        // Add wrapper for actions
        this.actionsWrapper = document.createElement("div");
        this.actionsWrapper.id = "ahrb-table-actions-wrapper";
        this.actionsWrapper.classList.add("mt-5", "d-flex", "justify-content-between");
        this.wrapper.appendChild(this.actionsWrapper);
    }

    /**
     * Display no data message if there's no data
     * and create table and fill it with data if we do
     * 
     * @param {string[]} fields
     * @param {string[][]} rows
     * @param {number} count Total number of available rows
     * @param {number} page Current page
     */
    displayData(fields: Array<string>, rows: Array<Array<string>>, count: number, page: number): void {
        // Clear table wrapper
        this.tableWrapper.innerHTML = "";

        // Clear pagination wrapper
        this.paginationWrapper.innerHTML = "";

        // Clear actions wrapper
        this.actionsWrapper.innerHTML = "";

        // Display no data message if there's no data
        if (rows.length === 0) {
            const noDataMessage = document.createElement("p");
            noDataMessage.classList.add("ahrb-no-data-message");
            noDataMessage.innerText = "No data";
            this.tableWrapper.appendChild(noDataMessage);
            return;
        }

        // Display table
        this.displayTable(fields, rows);

        // Change font size if necessary
        this.changeFontSizeOfTable();

        // Display pagination
        this.displayPagination(count, page);

        // Display actions
        this.displayActions();
    }

    /**
     * Display Table
     * 
     * @param {string[]} fields
     * @param {string[][]} rows
     */
    displayTable(fields: Array<string>, rows: Array<Array<string>>): void {
        const table = document.createElement("table");
        table.classList.add("table", "table-striped", "ad-hoc-report-table");

        // Add header
        const header = document.createElement("thead");
        const headerRow = document.createElement("tr");

        for (const field of fields) {
            const headerCell = document.createElement("th");
            headerCell.innerHTML = field;
            headerRow.appendChild(headerCell);
        }

        header.appendChild(headerRow);
        table.appendChild(header);

        // Add body
        const body = document.createElement("tbody");

        for (const row of rows) {
            const bodyRow = document.createElement("tr");

            for (const cell of row) {
                const bodyCell = document.createElement("td");
                bodyCell.innerHTML = cell;
                bodyRow.appendChild(bodyCell);
            }

            body.appendChild(bodyRow);
        }

        table.appendChild(body);

        // Add table to wrapper
        this.tableWrapper.appendChild(table);
    }

    /**
     * Change font size of table if it's bigger than its parent
     */
    changeFontSizeOfTable() {
        if (this.tableWrapper.querySelector('table').offsetWidth > this.tableWrapper.offsetWidth) {
            this.tableWrapper.classList.add('font-size-14px');
        } else {
            this.tableWrapper.classList.remove('font-size-14px');
        }
    }

    /**
     * Display pagination
     * 
     * @param {number} count Total number of available rows
     * @param {number} page Current page
     */
    displayPagination(count: number, page: number): void {
        // Calculate number of pages
        const pages = Math.ceil(count / 20);

        // Don't display pagination if there's only one page
        if (pages === 1) return;

        // Add pagination items
        for (let i = 1; i <= pages; i++) {
            const paginationItem = document.createElement("a");
            paginationItem.classList.add("ahrb-pagination-item", "mx-2", "text-secondary");
            paginationItem.setAttribute("href", "#");
            paginationItem.innerText = i.toString();

            // Add event listener for paginating
            paginationItem.addEventListener("click", this.handlePaginationClick.bind(this, paginationItem, i));

            if (i === page) {
                paginationItem.classList.add("active");
            }

            this.paginationWrapper.appendChild(paginationItem);
        }
    }

    /**
     * Handle pagination click
     * 
     * @param {HTMLElement} paginationItem
     * @param {number} page
     * @param {Event} event
     */
    handlePaginationClick(paginationItem: HTMLElement, page: number, event: Event): void {
        event.preventDefault();

        // Remove active class from all pagination items
        const paginationItems = this.paginationWrapper.querySelectorAll(".ahrb-pagination-item");
        paginationItems.forEach((item: HTMLElement) => {
            item.classList.remove("active");
        });

        // Add active class to clicked pagination item
        paginationItem.classList.add("active");

        // Call parent's report submit handler
        this.parent.onReportSubmit(page);
    }

    /**
     * Display actions
     */
    displayActions(): void {
        // Add export to CSV button
        this.exportToCsvButton = document.createElement("input");
        this.exportToCsvButton.type = "submit";
        this.exportToCsvButton.classList.add("gform_button", "button");
        this.exportToCsvButton.value = "Export to CSV";

        // Add event listener for exporting to CSV
        this.exportToCsvButton.addEventListener("click", this.handleCsvExport.bind(this));
        
        this.actionsWrapper.appendChild(this.exportToCsvButton);

        // Add save report button
        this.saveReportButton = document.createElement("input");
        this.saveReportButton.type = "submit";
        this.saveReportButton.classList.add("gform_button", "button");
        this.saveReportButton.value = "Save Report";

        // Add event listener for saving report
        this.saveReportButton.addEventListener("click", this.handleReportSave.bind(this));

        this.actionsWrapper.appendChild(this.saveReportButton);
    }

    /**
     * Handle CSV export click
     * 
     * @param {Event} event
     */
    handleCsvExport(event: Event): void {
        event.preventDefault();

        // Call parent's CSV export handler
        this.parent.onCsvExport();
    }

    /**
     * Handle report save click
     * 
     * @param {Event} event
     */
    handleReportSave(event: Event): void {
        event.preventDefault();

        // Call parent's report save handler
        this.parent.onReportSave();
    }

}
